import { supabase, getCurrentUser, getCurrentSession } from '../lib/supabaseClient';
import { User } from '../types';

/**
 * Authentication Service
 * Handles all authentication operations using Supabase Auth
 * Supports: Email/Password and Microsoft SSO (Azure AD)
 */

export interface AuthResponse {
  success: boolean;
  message: string;
  user?: User;
  error?: any;
}

export interface SSOProvider {
  provider: 'azure' | 'google' | 'microsoft';
  options?: {
    scopes?: string;
    queryParams?: Record<string, string>;
  };
}

// Sign in with email and password
export const signIn = async (
  email: string,
  password: string
): Promise<AuthResponse> => {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return {
        success: false,
        message: error.message || 'Invalid email or password',
        error,
      };
    }

    if (!data.user) {
      return {
        success: false,
        message: 'Authentication failed',
      };
    }

    // Fetch user profile from public.users table
    const userProfile = await getUserProfile(data.user.id);

    return {
      success: true,
      message: 'Sign in successful',
      user: userProfile || undefined,
    };
  } catch (error) {
    console.error('Error in signIn:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error,
    };
  }
};

// Sign in with Microsoft SSO (Azure AD)
export const signInWithMicrosoft = async (
  redirectTo?: string
): Promise<{ success: boolean; error?: any }> => {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        scopes: 'email profile openid',
        redirectTo: redirectTo || `${window.location.origin}/admin/dashboard`,
        queryParams: {
          prompt: 'select_account', // Always show account picker
        },
      },
    });

    if (error) {
      console.error('Error signing in with Microsoft:', error);
      return {
        success: false,
        error,
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error in signInWithMicrosoft:', error);
    return {
      success: false,
      error,
    };
  }
};

// Sign in with Google SSO (optional - for future use)
export const signInWithGoogle = async (
  redirectTo?: string
): Promise<{ success: boolean; error?: any }> => {
  try {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectTo || `${window.location.origin}/admin/dashboard`,
      },
    });

    if (error) {
      console.error('Error signing in with Google:', error);
      return {
        success: false,
        error,
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    console.error('Error in signInWithGoogle:', error);
    return {
      success: false,
      error,
    };
  }
};

// Sign up with email and password
export const signUp = async (
  email: string,
  password: string,
  name: string,
  entityId: string,
  role: 'admin' | 'hr' | 'recruiter' = 'hr'
): Promise<AuthResponse> => {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          entity_id: entityId,
          role,
        },
      },
    });

    if (error) {
      return {
        success: false,
        message: error.message || 'Sign up failed',
        error,
      };
    }

    if (!data.user) {
      return {
        success: false,
        message: 'Sign up failed',
      };
    }

    // The trigger will automatically create the user profile
    // Wait a moment for the trigger to complete
    await new Promise(resolve => setTimeout(resolve, 1000));

    const userProfile = await getUserProfile(data.user.id);

    return {
      success: true,
      message: 'Sign up successful. Please check your email for verification.',
      user: userProfile || undefined,
    };
  } catch (error) {
    console.error('Error in signUp:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error,
    };
  }
};

// Sign out
export const signOut = async (): Promise<boolean> => {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      console.error('Error signing out:', error);
      return false;
    }

    // Clear local storage
    localStorage.removeItem('authToken');

    return true;
  } catch (error) {
    console.error('Error in signOut:', error);
    return false;
  }
};

// Get current authenticated user
export const getAuthUser = async (): Promise<User | null> => {
  try {
    const authUser = await getCurrentUser();

    if (!authUser) {
      return null;
    }

    return await getUserProfile(authUser.id);
  } catch (error) {
    console.error('Error in getAuthUser:', error);
    return null;
  }
};

// Get user profile from public.users table
export const getUserProfile = async (userId: string): Promise<User | null> => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .eq('is_active', true)
      .single();

    if (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }

    // Fetch user's entity assignments
    let entityIds: string[] = [];
    try {
      const { data: ueData } = await supabase
        .from('user_entities')
        .select('entity_id')
        .eq('user_id', userId);
      if (ueData && ueData.length > 0) {
        entityIds = ueData.map((ue: any) => ue.entity_id);
      }
    } catch {
      // user_entities table may not exist yet; fall back gracefully
    }

    // Map database fields (snake_case) to interface fields (camelCase)
    return {
      id: data.id,
      email: data.email,
      name: data.name,
      role: data.role,
      entityId: data.entity_id,
      entityIds: entityIds.length > 0 ? entityIds : (data.entity_id ? [data.entity_id] : []),
      permissions: data.permissions || [],
      avatarUrl: data.avatar_url,
      createdAt: data.created_at,
      lastLoginAt: data.last_login_at
    } as User;
  } catch (error) {
    console.error('Error in getUserProfile:', error);
    return null;
  }
};

// Update user profile
export const updateUserProfile = async (
  userId: string,
  updates: Partial<User>
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('users')
      .update({
        ...(updates.name && { name: updates.name }),
        ...(updates.role && { role: updates.role }),
      })
      .eq('id', userId);

    if (error) {
      console.error('Error updating user profile:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in updateUserProfile:', error);
    return false;
  }
};

// Reset password (request)
export const requestPasswordReset = async (
  email: string
): Promise<AuthResponse> => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      return {
        success: false,
        message: error.message || 'Password reset request failed',
        error,
      };
    }

    return {
      success: true,
      message: 'Password reset email sent. Please check your inbox.',
    };
  } catch (error) {
    console.error('Error in requestPasswordReset:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error,
    };
  }
};

// Update password
export const updatePassword = async (
  newPassword: string
): Promise<AuthResponse> => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      return {
        success: false,
        message: error.message || 'Password update failed',
        error,
      };
    }

    return {
      success: true,
      message: 'Password updated successfully',
    };
  } catch (error) {
    console.error('Error in updatePassword:', error);
    return {
      success: false,
      message: 'An unexpected error occurred',
      error,
    };
  }
};

// Check if user is authenticated
export const isAuthenticated = async (): Promise<boolean> => {
  try {
    const session = await getCurrentSession();
    return !!session;
  } catch (error) {
    console.error('Error in isAuthenticated:', error);
    return false;
  }
};

// Get user's entity ID
export const getUserEntityId = async (): Promise<string | null> => {
  try {
    const user = await getAuthUser();
    return user?.entityId || null;
  } catch (error) {
    console.error('Error in getUserEntityId:', error);
    return null;
  }
};

// Check if user has a specific role
export const hasRole = async (
  requiredRole: 'admin' | 'hr' | 'recruiter'
): Promise<boolean> => {
  try {
    const user = await getAuthUser();
    return user?.role === requiredRole;
  } catch (error) {
    console.error('Error in hasRole:', error);
    return false;
  }
};

// Check if user has admin role
export const isAdmin = async (): Promise<boolean> => {
  return await hasRole('admin');
};

// Check if user has permission
export const hasPermission = async (permission: string): Promise<boolean> => {
  try {
    const user = await getAuthUser();

    if (!user) {
      return false;
    }

    // Admins have all permissions
    if (user.role === 'admin') {
      return true;
    }

    // Check if user has the specific permission
    return user.permissions?.includes(permission) || false;
  } catch (error) {
    console.error('Error in hasPermission:', error);
    return false;
  }
};

// Listen to auth state changes
export const onAuthStateChange = (
  callback: (event: string, session: any) => void
) => {
  return supabase.auth.onAuthStateChange(callback);
};
